import { useCallback, useRef, useState } from "react";
import { useDebounce } from "@tellescope/react-hooks";
import { resolveDraftAutosave } from "../utils";
import { useDraftMutations, type DraftCreateFields } from "./use-draft-mutations";

export type DraftAutosaveStatus = "idle" | "saving" | "saved";

export interface UseDraftAutosaveArgs {
  loadedContent: string | undefined;
  draftId: string | undefined;
  isEditingScheduled: boolean;
  buildCreateFields: () => DraftCreateFields | undefined;
  debounceMs?: number;
  showWhileWaiting?: boolean;
}

const DEFAULT_DEBOUNCE_MS = 1000;

export const useDraftAutosave = ({
  loadedContent,
  draftId,
  isEditingScheduled,
  buildCreateFields,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  showWhileWaiting = false,
}: UseDraftAutosaveArgs) => {
  const [status, setStatus] = useState<DraftAutosaveStatus>("idle");

  const createdDraftIdRef = useRef<string>(undefined);
  const savedContentRef = useRef<string>(undefined);
  const deletedDraftIdsRef = useRef(new Set<string>());

  const propDraftId = draftId && !deletedDraftIdsRef.current.has(draftId) ? draftId : undefined;
  const resolvedDraftId = propDraftId ?? createdDraftIdRef.current;
  const resolvedSavedContent = propDraftId ? loadedContent : savedContentRef.current ?? loadedContent;

  const propsRef = useRef({ resolvedDraftId, resolvedSavedContent, isEditingScheduled });
  propsRef.current = { resolvedDraftId, resolvedSavedContent, isEditingScheduled };

  const { saveDraft, deleteDraft } = useDraftMutations();

  const getAction = useCallback((message: string) => {
    const { resolvedDraftId, resolvedSavedContent, isEditingScheduled } = propsRef.current;
    return resolveDraftAutosave({
      message,
      loadedContent: resolvedSavedContent,
      draftId: resolvedDraftId,
      isEditingScheduled,
    });
  }, []);

  const save = useCallback(
    async (message: string) => {
      const action = getAction(message);
      if (action.type === "skip") return;

      const fields = buildCreateFields();
      if (!fields) return;

      if (action.type === "delete") {
        await deleteDraft({ id: action.id, channel: fields.channel });
        deletedDraftIdsRef.current.add(action.id);
        createdDraftIdRef.current = undefined;
        savedContentRef.current = "";
        return;
      }

      setStatus("saving");
      const savedId = await saveDraft({
        message: action.message,
        fields,
        ...(action.type === "update" ? { id: action.id } : {}),
      });

      if (!savedId) {
        setStatus("idle");
        return;
      }

      createdDraftIdRef.current = savedId;
      savedContentRef.current = action.message;
      setStatus("saved");
    },
    [buildCreateFields, getAction, deleteDraft, saveDraft]
  );

  const { debounced, cancel, flush } = useDebounce(save, debounceMs);

  const onMessageChange = useCallback(
    (message: string) => {
      const actionType = getAction(message).type;
      if (actionType === "create" || actionType === "update") {
        setStatus(showWhileWaiting ? "idle" : "saving");
      } else if (actionType === "delete") {
        setStatus("idle");
      }
      debounced(message);
    },
    [debounced, getAction, showWhileWaiting]
  );

  const takeDraftForSend = useCallback(() => {
    cancel();
    setStatus("idle");
    const draftIdToSend = propsRef.current.resolvedDraftId;
    createdDraftIdRef.current = undefined;
    savedContentRef.current = undefined;
    return draftIdToSend;
  }, [cancel]);

  return { onMessageChange, flush, takeDraftForSend, status };
};
